# Wellness Frontier Consumer Health Data Privacy Policy

**Effective date:** September 1, 2026  
**Last updated:** October 27, 2026

This policy describes how Wellness Frontier, operated by Advanced Health and
Wellness Group, collects, uses, and shares consumer health data as those terms
are defined by the Washington My Health My Data Act.

## 1. Categories of consumer health data we collect and why

| Category | Examples | Purpose |
|---|---|---|
| Bodily functions, vital signs, and measurements | steps, sleep duration, resting heart rate, water you log | show your measurements and progress, support goals, and operate the wellness features you request |
| Past measurements sent once | your step totals and time asleep for the 14 finished days before you first connect a health source | start your own Wellness Score, so it works from the day you connect. They never earn points |
| A score worked out from the data above | your own Wellness Score, out of 100, from your steps, sleep, water, lessons finished, and thanks sent | show you your own score, and give your employer one company-wide average each month |
| Device and app check tied to step uploads | the result of a Google or Apple check that the app is genuine and the phone is real, and a scrambled identifier for the app's installation | confirm that steps came from the real app on a real phone before they earn points, and keep one phone earning for one employee at a time |
| Health and wellness responses | answers you choose to provide in a health or wellness survey | provide your wellness experience and create employer reports that contain participation counts, not your answers |
| Wellness interests and choices | the wellness area you voluntarily select as most important for the month | personalize the program and create company-wide monthly totals |
| Wellness-program activity | completed actions, connected-source status, goals, points, achievements, and days-in-a-row progress | operate the program, recognize participation, and create the limited employer reports described in our general Privacy Policy |
| Food and weight records you keep | what you ate or drank, how much, the date and time, and calories if you add them; a weight you type and its date | show your own records to you. Only you can see them |
| Action plans you write | the goal and actions you write after a library video, and the yes or no marks for the seven days after you finish | show your own plan to you. Only you can see what you wrote |
| Facts about your records and videos | the days you added a food or weight entry, not which of the two. The video and month of an action plan you finished. The days you finished a plan. The library videos you finished each month | count toward your Education goal, points, and badges. Your employer does not see these facts |
| Benefits and care-seeking questions | a question you submit to an enabled benefits coach | answer the question from employer plan documents and improve the coach using a de-identified question |
| AI coach chats | what you type to the AI trainer, AI nutrition guide or AI life coach, and, for the trainer and nutrition guide, a summary of your last 7 days of steps and workouts from a connected health app | reply to you in the chat. We do not keep the chat |
| Coach notes | a short summary of your exercise, eating and habit goals, routines, likes, and progress, written by the AI coaches, which you can change or delete | let the AI coaches pick up where you left off |
| Information that identifies health-service use | use of health-related app features | provide the feature, protect the service, and meet legal obligations |

We also process identifiers, such as your name, work email, and account
reference, when needed to associate consumer health data with your account.

**Signing in with Face ID or a fingerprint.** If you turn this on, your phone makes a sign-in key that only your phone's Face ID or fingerprint can unlock. Your phone checks your face or fingerprint itself; we never see, receive or keep it, and neither does your employer. We keep only the public part of the key, the kind of phone, and when it was made and last used. We delete it when you sign out on that phone, turn it off, change your sign-in email, are removed from your employer's staff list or delete your account, or after 90 days without use. We email you when it is turned on.

## 2. Sources of consumer health data

We collect consumer health data from:

- you, when you answer a question, set a goal, choose a wellness area, or use a
  program feature;
- Apple Health or Health Connect, after you approve specific read access,
  including the 14 finished days sent once when you first connect;
- your phone, when it confirms to us that the app is genuine and the phone is
  real before your steps earn points;
- program records created when you complete an action, earn points, or use a
  feature; and
- your employer, for account and workplace information used to make the
  program available to you.

## 3. Categories of consumer health data we share

We share the following categories only as needed to provide the service you
request or as otherwise permitted by law:

- consumer health data processed by our contracted service providers for
  hosting, databases, storage, email delivery, security, support, document
  processing, search, the AI coach, the AI trainer, the AI nutrition guide and
  the AI life coach;
- named program-participation information provided to your employer, including
  your name, points, and place on the points board if you have points, unless
  you take your name off that board in the app;
- participation counts and group-level wellness-program totals provided to
  your employer;
- one company-wide average Wellness Score each month, and how many people it
  covers, provided to your employer. This average has no minimum group size,
  and your employer never receives your own score;
- what Google or Vimeo receives when a video player loads. Library videos play
  in YouTube's player. A challenge video plays in YouTube's or Vimeo's player.
  They receive the video, the time, your IP address, details about your device,
  and the web address of the app. The app does not give Google or Vimeo your
  name, your email address, or your account;
- information about the app and the phone, and a scrambled fingerprint of a
  step upload, sent to Google on Android or Apple on iPhone to confirm the app
  and the phone. Neither receives your steps; and
- consumer health data required by valid legal process.

We do not share your individual connected-health measurements, survey answers,
coach or benefits questions, medical history, or sensitive-support-resource
use with your employer. We do not sell consumer health data.

Your employer also never receives your food, weight, or action plan records.
It never receives which library videos you watched.

Your employer's admin can change the email you sign in with. Whoever can read
mail at the new address can then sign in and see your health data. Section 4 of
our Privacy Policy explains this.

## 4. Third parties and affiliates

The categories of third parties with which we share consumer health data are:

- **Microsoft Corporation and its Azure services**, as contracted processors for
  cloud hosting, databases, storage, email delivery, security, document
  processing, search, and Azure OpenAI when the coach, the AI trainer, the AI
  nutrition guide or the AI life coach is enabled. Where the coach is enabled,
  Microsoft keeps a copy of the question submitted to it and the answer
  returned. Where the AI trainer, AI nutrition guide or AI life coach is
  enabled, Microsoft may keep a copy of each chat, the coach notes and any
  summary of recent steps and workouts sent with it, and the replies. Authorized
  Microsoft personnel may review either copy to detect abuse and misuse of their
  service. Microsoft keeps it for a limited period and then deletes it. We have
  not been granted an exemption from this monitoring. That copy is held by
  Microsoft, not by us: we cannot read it and we cannot delete it in response to
  a request made to us;
- **Google LLC** (on Android) and **Apple Inc.** (on iPhone), which confirm that
  the app is genuine and the phone is real before steps earn points. They
  receive information about the app and the phone and a scrambled fingerprint of
  the upload, never your steps, and they process it under their own terms;
- **Google LLC**, through its YouTube player, and **Vimeo**, through its
  player, when a video player loads. Library videos play in YouTube's
  player. A challenge video plays in YouTube's or Vimeo's player, which loads
  when the challenge is on your screen. Each receives which video it is and
  when, your IP address, details about your device, and the web address of the
  app, which can include your employer's name. If you are signed in to Google
  or Vimeo on that device, it may know who you are by name. Each handles this
  under its own terms and keeps it for as long as its own rules say. YouTube
  may show ads that we do not choose;
- **your employer**, only for the employer-visible program information and
  group-level reports described above; and
- **government authorities or other recipients required by law**, when a valid
  legal requirement applies.

**Specific affiliates:** none.

## 5. How to exercise your rights

Subject to applicable law, you may:

- confirm whether we collect, share, or sell consumer health data about you;
- access your consumer health data and receive a list of third parties and
  affiliates with which it was shared or sold, including a way to contact them;
- withdraw consent to future collection or sharing; and
- request deletion of consumer health data, including notice of the request to
  processors and other recipients as required by law.

You may use the app’s Account area to disconnect a health source or request
account deletion where that control is available. You may exercise any right,
or appeal a refusal to act, by emailing **privacy@wellnessfrontier.ai**. You do
not need to create a new account to make a request. We may ask for information
reasonably needed to authenticate you and the request.

We will respond without undue delay and within 45 days. When reasonably
necessary, we may extend the response period once by up to 45 additional days
and will tell you why during the first 45 days. Responses are free up to twice
per year unless a request is manifestly unfounded, excessive, or repetitive.

If we deny an appeal, we will explain the decision in writing and provide a way
to contact the Washington State Attorney General.

